import React, { useState } from "react";
import { ChevronDown, ChevronRight, Server, Network, Shield, Route } from "lucide-react";
import { wib, ageMin, mbps } from "./useFireboxLive";
import { derive } from "./WgRemoteTopology";

// System-manager style tree for a REMOTE Firebox (Medan / Dumai), from its own SNMP data (fireboxRemote.js):
// interfaces (zone inferred from routing), BOVPN tunnels grouped by peer (IPsec SAs), VPN interfaces and the route table.

const mono = { fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace", fontSize: 12 };
const card = { background: "var(--card-bg, #fff)", border: "1px solid #e2e8f0", borderRadius: 12, padding: 16 };
const fmt = (n) => (n == null ? "-" : Number(n).toLocaleString("en-US"));
const human = (b) => { if (b == null) return "-"; const u = ["B", "KB", "MB", "GB", "TB"]; let i = 0, v = Number(b); while (v >= 1000 && i < 4) { v /= 1000; i++; } return v.toFixed(i ? 1 : 0) + " " + u[i]; };
const ZC = { External: "#16a34a", Trusted: "#16a34a", VPN: "#16a34a", Disabled: "#94a3b8", Unused: "#94a3b8" };

function Node({ label, icon, open: o0 = false, dot, dim, children, right }) {
  const [open, setOpen] = useState(o0);
  const has = !!children && (!Array.isArray(children) || children.some(Boolean));
  return (
    <div>
      <div onClick={() => has && setOpen(!open)} style={{ display: "flex", alignItems: "center", gap: 6, padding: "2px 4px", borderRadius: 4, cursor: has ? "pointer" : "default", color: dim ? "#94a3b8" : "#0f172a", ...mono }}>
        <span style={{ width: 14, display: "inline-flex" }}>{has ? (open ? <ChevronDown size={13} /> : <ChevronRight size={13} />) : null}</span>
        {icon}
        {dot && <span style={{ width: 8, height: 8, borderRadius: 2, background: dot, flexShrink: 0 }} />}
        <span style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{label}</span>
        {right && <span style={{ marginLeft: "auto", color: "#64748b", whiteSpace: "nowrap", paddingLeft: 8 }}>{right}</span>}
      </div>
      {has && open && <div style={{ marginLeft: 11, paddingLeft: 10, borderLeft: "1px dotted #cbd5e1" }}>{children}</div>}
    </div>
  );
}
const Leaf = ({ k, v, arrow }) => (
  <div style={{ display: "flex", gap: 6, padding: "1px 4px", ...mono, color: "#334155" }}>
    <span style={{ width: 14, textAlign: "center", color: arrow ? "#0ea5e9" : "#94a3b8" }}>{arrow ? (arrow === "out" ? "→" : "←") : "•"}</span>
    <span style={{ color: "#64748b" }}>{k}:</span><span style={{ wordBreak: "break-word" }}>{v}</span>
  </div>
);

function IfNode({ f, label, open }) {
  const off = f.zone === "Disabled" || f.zone === "Unused";
  const live = f.rate && (f.rate.inMbps != null || f.rate.outMbps != null);
  return (
    <Node open={open} dim={off} dot={off ? ZC[f.zone] : f.oper === "down" ? "#dc2626" : ZC[f.zone]}
      label={label}
      right={off ? null : live ? `↑${mbps(f.rate.outMbps)} ↓${mbps(f.rate.inMbps)}` : f.out != null ? `↑${human(f.out)} ↓${human(f.in)}` : null}>
      {f.gw && <Leaf k="Gateway" v={f.gw + (f.defaultRoute ? " (default route)" : "")} />}
      {(f.ips || []).length ? <Leaf k="IP address" v={f.ips.join(", ")} /> : null}
      {(f.nets || []).length ? <Leaf k="Connected" v={f.nets.join(", ")} /> : null}
      {f.endpoint && <Leaf k="BOVPN endpoint" v={f.endpoint} />}
      {(f.routes || []).length ? (
        <Node label={`Static routes (${f.routes.length})`} dim={false}>
          {f.routes.map((r) => <Leaf key={r} k="Route" v={r} />)}
        </Node>
      ) : null}
      {f.mac && <Leaf k="MAC" v={f.mac} />}
      {f.speedMbps ? <Leaf k="Link speed" v={f.speedMbps >= 1000 ? f.speedMbps / 1000 + " Gbps" : f.speedMbps + " Mbps"} /> : null}
      {live ? <Leaf k="Throughput" v={`↑ ${mbps(f.rate.outMbps)} · ↓ ${mbps(f.rate.inMbps)} (5-min avg)`} /> : null}
      <Leaf k="Sent" v={`${fmt(f.out)} bytes${f.outPkts != null ? ` (${fmt(f.outPkts)} packets)` : ""}`} arrow="out" />
      <Leaf k="Received" v={`${fmt(f.in)} bytes${f.inPkts != null ? ` (${fmt(f.inPkts)} packets)` : ""}`} arrow="in" />
    </Node>
  );
}

export default function WgRemoteSystemManager({ S, L, prev, error }) {
  if (!L) return null;
  const { IF, VIF, groups, vifPeers, sas } = derive(L, prev, S);
  const routes = (L.routes || []).slice().sort((a, b) => (a.dest === "0.0.0.0/0" ? -1 : b.dest === "0.0.0.0/0" ? 1 : String(a.dest).localeCompare(String(b.dest), undefined, { numeric: true })));
  const model = S.model.replace(/^Firebox /, "");
  return (
    <div style={card}>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexWrap: "wrap", marginBottom: 10 }}>
        <Server size={20} color="rgb(var(--brand-600, 13 148 136))" />
        <div style={{ marginRight: "auto" }}>
          <div style={{ fontSize: 16, fontWeight: 700 }}>WatchGuard System Manager — {S.label}</div>
          <div style={{ fontSize: 12, color: "#64748b" }}>Interfaces, Branch Office VPN tunnels and routes · live SNMP via Jakarta LAN · checked {wib(L.checked_at)}{ageMin(L.checked_at) > 15 ? " (stale)" : ""}{error ? " · last poll error: " + error : ""} · click a node to expand</div>
        </div>
        <span style={{ fontSize: 11, color: "#64748b", display: "inline-flex", gap: 10 }}>
          <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: "#16a34a", marginRight: 4 }} />active / available</span>
          <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: "#dc2626", marginRight: 4 }} />link down</span>
          <span><span style={{ display: "inline-block", width: 9, height: 9, borderRadius: 2, background: "#94a3b8", marginRight: 4 }} />disabled / unused</span>
        </span>
      </div>
      <div style={{ ...mono, fontWeight: 700, padding: "6px 8px", background: "#f8fafc", border: "1px solid #e2e8f0", borderRadius: 8, marginBottom: 10, display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <Shield size={14} color="#dc2626" /> {(L.sys && L.sys.name) || S.host} - WatchGuard {model} [Fireware OS v{(L.sys && L.sys.version) || "-"}]
        <span style={{ fontWeight: 400, color: "#64748b", marginLeft: "auto" }}>{L.sys && L.sys.activeConns != null ? fmt(L.sys.activeConns) + " active connections" : ""}</span>
      </div>
      <div className="wgsm2-grid" style={{ display: "grid", gridTemplateColumns: "minmax(0,1fr) minmax(0,1fr)", gap: 16 }}>
        <style>{`@media (max-width: 900px){ .wgsm2-grid{ grid-template-columns: minmax(0,1fr) !important; } }`}</style>
        <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: 8, overflowX: "auto" }}>
          <Node label="Interfaces (Routed Mode)" icon={<Network size={14} color="#0369a1" />} open>
            {IF.map((f) => (
              <IfNode key={f.idx} f={f} open={f.defaultRoute}
                label={`eth${f.eth}: ${f.name} [${f.zone}${f.oper === "down" && f.zone !== "Disabled" && f.zone !== "Unused" ? ":Down" : ""}]${(f.ips && f.ips[0]) ? " - " + f.ips[0] : (f.nets && f.nets[0]) ? " - " + f.nets[0] : ""}`} />
            ))}
            {VIF.length ? (
              <Node label={`VPN Interfaces (${VIF.filter((v) => v.oper === "up").length}/${VIF.length} up)`} open>
                {VIF.map((v) => <IfNode key={v.idx} f={v} label={`${v.name} [${v.oper === "up" ? "Up" : "Down"}]${(v.ips && v.ips[0]) ? " - " + v.ips[0] : ""}`} />)}
                {vifPeers.length ? <Leaf k="IPsec peers (0.0.0.0/0 ↔ 0.0.0.0/0)" v={vifPeers.join(", ")} /> : null}
              </Node>
            ) : null}
          </Node>
          <div style={{ fontSize: 11, color: "#94a3b8", padding: "6px 4px 0" }}>Zone shown is inferred from the routing table — SNMP does not expose WatchGuard zones.</div>
        </div>
        <div style={{ border: "1px solid #e2e8f0", borderRadius: 8, padding: 8, overflowX: "auto" }}>
          <Node label={`Branch Office VPN Tunnels (${sas.length} SAs)`} icon={<Shield size={14} color="#dc2626" />} open>
            {groups.map((g, gi) => (
              <Node key={g.peer} open={gi === 0} dot="#16a34a" label={`Gateway: ${g.name} (${g.peer}) - ${g.t.length} active tunnels`}>
                {g.t.map((x, i) => (
                  <div key={i} style={{ display: "flex", alignItems: "center", gap: 6, padding: "1px 4px", ...mono }}>
                    <span style={{ width: 14 }} />
                    <span style={{ width: 8, height: 8, borderRadius: 2, background: "#16a34a", flexShrink: 0 }} />
                    <span>Local: {x.local} Remote: {x.remote}{x.rekeys != null ? <span style={{ color: "#94a3b8" }}> · rekeys {x.rekeys}</span> : null}</span>
                  </div>
                ))}
              </Node>
            ))}
            {!groups.length && <div style={{ ...mono, color: "#94a3b8", padding: "2px 24px" }}>No IPsec SAs reported.</div>}
          </Node>
          <Node label={`Routes (${routes.length})`} icon={<Route size={14} color="#7e22ce" />}>
            {routes.map((r, i) => (
              <div key={i} style={{ display: "flex", gap: 6, padding: "1px 4px", ...mono, color: r.dest === "0.0.0.0/0" ? "#0f172a" : "#334155", fontWeight: r.dest === "0.0.0.0/0" ? 700 : 400 }}>
                <span style={{ width: 14, textAlign: "center", color: "#94a3b8" }}>•</span>
                <span style={{ minWidth: 150 }}>{r.dest}</span>
                <span style={{ color: "#64748b" }}>{r.hop ? "via " + r.hop : "connected"}</span>
                <span style={{ marginLeft: "auto", color: "#94a3b8", whiteSpace: "nowrap" }}>{r.ifName}</span>
              </div>
            ))}
          </Node>
        </div>
      </div>
    </div>
  );
}
